<template>
  <component
    :is="isStatic ? 'div' : 'v-dialog'"
    :value="value"
    v-bind="isStatic ? {} : { maxWidth: '90vw' }"
    @input="$emit('input', $event)"
  >
    <v-card :class="['bnk-dialog-card', { 'bnk-static-card': isStatic }]" :elevation="isStatic ? 0 : undefined">
      <div v-if="!isStatic" class="bnk-dialog-header">
        <span class="bnk-dialog-title">{{ title }}</span>
        <v-btn icon small @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <div class="bnk-dialog-body" :class="{ 'pa-0': isStatic }">
        <div class="bnk-wrap">
          <!-- Upgrade Button (Top Right) -->
          <v-btn
            v-if="showUpgrade"
            small
            depressed
            class="bnk-upgrade-btn"
            @click="$emit('upgrade')"
          >
            <v-icon left x-small>mdi-crown</v-icon>
            อัปเกรด
          </v-btn>

          <img :src="frameImage" class="bnk-frame" alt="" />

          <!-- Avatar circle (center) -->
          <div class="bnk-avatar" @click="$emit('change')">
            <img v-if="avatarUrl" :src="avatarUrl" class="bnk-avatar-img" alt="" />
            <template v-else>
              <span v-if="initials" class="bnk-initials">{{ initials }}</span>
              <v-icon v-else size="44" color="#9CA3AF">mdi-account-circle</v-icon>
            </template>
          </div>

          <!-- 1. ELSON III — below the banknote number (top-right) -->
          <div class="bnk-zone-brand" :style="brandStyle">ELSON III</div>

          <!-- 2. Pack name — centered in right zone -->
          <div class="bnk-zone-pack" :style="subStyle">
            <div class="bnk-pack-name">{{ packName || 'Aurora' }}</div>
            <div class="bnk-ref" :style="subStyle">{{ packInitial }}-{{ userRef || '000000000' }}</div>
          </div>

          <!-- 3. Alter input — bottom right -->
          <div class="bnk-zone-input" :style="alterBorderStyle">
            <input
              v-model="localAlterText"
              class="bnk-alter-input"
              :style="alterInputStyle"
              :placeholder="alterPlaceholder"
              maxlength="40"
              @keydown.enter="saveAlter"
            />
            <button class="bnk-alter-save" @click="saveAlter" title="บันทึก">
              <v-icon size="14" :color="theme.input">mdi-check</v-icon>
            </button>
          </div>
        </div>

        <!-- Referrer info — below the banknote frame -->
        <div v-if="referrerRef || referrerName || showDownlineButton" class="bnk-referrer-outside">
          <div class="bnk-referrer-info">
            <template v-if="referrerRef || referrerName">
              <div class="bnk-referrer-label">ผู้แนะนำ</div>
              <div class="bnk-referrer-row">
                <span class="bnk-referrer-field">
                  <span class="bnk-referrer-field-label">รหัส:</span>
                  <span class="bnk-referrer-code">{{ formattedReferrerRef || '-' }}</span>
                </span>
                <span class="bnk-referrer-field">
                  <span class="bnk-referrer-field-label">ชื่อ:</span>
                  <span class="bnk-referrer-name">{{ referrerName || '-' }}</span>
                </span>
              </div>
            </template>
            <template v-else>
              <div class="bnk-referrer-label">เครือข่ายของฉัน</div>
              <div class="bnk-referrer-field-label">ดูคนที่คุณแนะนำได้ที่นี่</div>
            </template>
          </div>
          <v-btn
            v-if="showDownlineButton"
            small depressed
            class="bnk-downline-btn"
            @click="downlineDialog = true"
          >
            <v-icon left x-small>mdi-sitemap</v-icon>
            ดูคนที่ฉันแนะนำ
          </v-btn>
        </div>

        <MyDownlineDialog v-if="showDownlineButton" v-model="downlineDialog" :role="downlineRole" />
      </div>

      <div v-if="!isStatic" class="bnk-dialog-actions">
        <v-btn outlined height="36" @click="$emit('input', false)">ปิด</v-btn>
        <v-btn class="bnk-btn-change" height="36" @click="$emit('change')">{{ changeLabel }}</v-btn>
      </div>
    </v-card>
  </component>
</template>

<script>
const FRAMES = {
  "Aurora":       "/img/Aurora.png",
  "Supercluster": "/img/Supercluster_.png",
  "The Galaxy":   "/img/The_Galaxy.png",
  "The Moon":    "/img/The_Star.png",
  "The Sun":      "/img/The_Sun.png",
};

/* main = brand color, sub = smaller text color, outline = shadow outline color */
const THEMES = {
  "Aurora":       { main: "#ede9fe", sub: "#ddd6fe", outline: "60,20,120",  input: "#c4b5fd" },
  "Supercluster": { main: "#fff8dc", sub: "#fef3c7", outline: "60,30,0",    input: "#fde68a" },
  "The Galaxy":   { main: "#e8eaf6", sub: "#c5cae9", outline: "10,20,80",   input: "#9fa8da" },
  "The Moon":    { main: "#f8fafc", sub: "#e2e8f0", outline: "20,30,60",   input: "#cbd5e1" },
  "The Sun":      { main: "#fff3e0", sub: "#ffe0b2", outline: "100,30,0",   input: "#ffcc80" },
};

import MyDownlineDialog from "@/components/Shared/MyDownlineDialog.vue";

export default {
  name: "BanknoteAvatarDialog",
  components: { MyDownlineDialog },
  props: {
    value:            { type: Boolean, default: false },
    avatarUrl:        { type: String,  default: "" },
    packName:         { type: String,  default: "Aurora" },
    title:            { type: String,  default: "รูปโปรไฟล์" },
    changeLabel:      { type: String,  default: "เปลี่ยนรูปภาพ" },
    initials:         { type: String,  default: "" },
    userRef:          { type: String,  default: "" },
    referrerRef:      { type: String,  default: "" },
    referrerName:     { type: String,  default: "" },
    referrerPackName: { type: String,  default: "" },
    alterText:        { type: String,  default: "" },
    alterPlaceholder: { type: String,  default: "ข้อความส่วนตัว..." },
    isStatic:         { type: Boolean, default: false },
    showUpgrade:      { type: Boolean, default: false },
    showDownlineButton: { type: Boolean, default: false },
    downlineRole:     { type: String, default: "user" },
  },

  data() {
    return { localAlterText: this.alterText || "", downlineDialog: false };
  },

  computed: {
    frameImage() {
      return FRAMES[this.packName] || FRAMES["Aurora"];
    },
    packInitial() {
      return (this.packName || "A").charAt(0).toUpperCase();
    },
    referrerPackInitial() {
      return (this.referrerPackName || "A").charAt(0).toUpperCase();
    },
    formattedReferrerRef() {
      if (!this.referrerRef) return "";
      return `${this.referrerPackInitial}-${this.referrerRef}`;
    },
    theme() {
      return THEMES[this.packName] || THEMES["Aurora"];
    },

    panelStyle() {
      return { background: "none", border: "none" };
    },
    brandStyle() {
      const { main, outline } = this.theme;
      const o = `rgba(${outline},0.95)`;
      return {
        color: main,
        textShadow: [
          `-1px -1px 0 ${o}`, `1px -1px 0 ${o}`,
          `-1px  1px 0 ${o}`, `1px  1px 0 ${o}`,
          `0 0 10px rgba(${outline},0.5)`,
        ].join(", "),
      };
    },
    subStyle() {
      const { sub, outline } = this.theme;
      const o = `rgba(${outline},0.9)`;
      return {
        color: sub,
        textShadow: `-1px -1px 0 ${o}, 1px -1px 0 ${o}, -1px 1px 0 ${o}, 1px 1px 0 ${o}`,
      };
    },
    alterBorderStyle() {
      const { outline } = this.theme;
      return { borderColor: `rgba(${outline}, 0.4)` };
    },
    alterInputStyle() {
      const { input } = this.theme;
      return { color: input, caretColor: input };
    },
  },

  watch: {
    alterText(v) { this.localAlterText = v || ""; },
  },

  methods: {
    saveAlter() { this.$emit("save-alter", this.localAlterText); },
  },
};
</script>

<style scoped>
.bnk-dialog-card { border-radius: 16px !important; overflow: hidden; }
.bnk-static-card { background: transparent !important; border: none !important; box-shadow: none !important; }

.bnk-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 8px;
}
.bnk-dialog-title { font-size: 16px; font-weight: 700; color: #1e293b; }

.bnk-dialog-body { padding: 16px 0; display: flex; flex-direction: column; align-items: center; }

.bnk-wrap { width: 100%; position: relative; container-type: inline-size; }
.bnk-frame { width: 100%; height: auto; display: block; pointer-events: none; border-radius: 16px; }

/* Upgrade Button */
.bnk-upgrade-btn {
  position: absolute;
  top: 10px;
  left: 15px;
  z-index: 10;
  border-radius: 20px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
  background: rgba(255, 255, 255, 0.9) !important;
  color: #F4811F !important;
}

/* Avatar */
.bnk-avatar {
  position: absolute;
  top: 50%; left: 50.25%;
  transform: translate(-50%, -50%);
  width: 24.5%; aspect-ratio: 1 / 1;
  border-radius: 50%; overflow: hidden;
  border: 3px solid rgba(255,255,255,0.9);
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
  background: #f8fafc;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: transform 0.2s;
}
.bnk-avatar:hover {
  transform: translate(-50%, -50%) scale(1.05);
}
.bnk-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.bnk-initials { font-size: 22px; font-weight: 700; color: #2563eb; }

/* ── Zone 1: ELSON III — below the big number, top-right ── */
.bnk-zone-brand {
  position: absolute;
  top: 35%;
  right: 8%;
  width: 40%;
  font-size: clamp(9px, 4.5cqw, 36px);
  font-weight: 900;
  letter-spacing: 0.06em;
  line-height: 1.0;
  text-align: right;
  pointer-events: none;
}

/* ── Zone 2: Pack name — center of right area ── */
.bnk-zone-pack {
  position: absolute;
  top: 52.5%;
  right: 0%;
  width: 36%;
  text-align: center;
  font-size: clamp(8px, 3.8cqw, 30px);
  line-height: 1.3;
  pointer-events: none;
}
.bnk-pack-name {
  font-weight: 900;
  font-size: 1.1em;
  display: block;
  line-height: 0.9;
  letter-spacing: -0.02em;
}
.bnk-ref {
  font-size: 0.72em;
  font-family: monospace;
  font-weight: 800;
  letter-spacing: 0.12em;
  opacity: 1;
  margin-top: 0.25em;
  display: block;
}
.bnk-referrer-outside {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  color: #1f2a39;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
}
.bnk-referrer-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}
.bnk-downline-btn {
  border-radius: 20px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  background: #FFF7ED !important;
  color: #F4811F !important;
  border: 1px solid #FDBA74 !important;
  flex-shrink: 0;
}
.bnk-referrer-label {
  font-weight: 700;
  color: #6b7280;
  font-size: 12px;
  letter-spacing: 0.04em;
}
.bnk-referrer-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.bnk-referrer-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.bnk-referrer-field-label {
  color: #6b7280;
  font-size: 13px;
}
.bnk-referrer-code {
  font-family: monospace;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #1f2a39;
}
.bnk-referrer-name {
  font-weight: 600;
  color: #1f2a39;
}

/* ── Zone 3: Alter input — bottom-right ── */
.bnk-zone-input {
  position: absolute;
  bottom: 4%;
  right: 3%;
  width: 30%;
  pointer-events: all;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0,0,0,0.30);
  border-radius: 5px;
  padding: 5px 8px;
  border: 1px solid;
  backdrop-filter: blur(2px);
  line-height: 1;
}
.bnk-alter-input {
  flex: 1;
  background: transparent;
  border: none; outline: none;
  font-size: clamp(8px, 3.5cqw, 24px);
  width: 100%; min-width: 0;
}
.bnk-alter-input::placeholder { opacity: 0.35; }

/* ── Tablet (≤ 960px) ── */
@media (max-width: 960px) {
  .bnk-upgrade-btn { font-size: 11px !important; top: 7px; left: 10px; }
  .bnk-zone-input  { padding: 4px 6px; gap: 3px; border-radius: 4px; }
  .bnk-alter-save .v-icon { font-size: 12px !important; }
}

/* ── Mobile (≤ 600px) ── */
@media (max-width: 600px) {
  .bnk-upgrade-btn { font-size: 10px !important; top: 5px; left: 7px; }
  .bnk-zone-input  { padding: 2px 5px; gap: 2px; border-radius: 3px; }
  .bnk-alter-save  { padding: 0; }
  .bnk-alter-save .v-icon { font-size: 10px !important; }
}
.bnk-alter-save {
  background: transparent; border: none;
  cursor: pointer; padding: 0 2px;
  display: flex; align-items: center; flex-shrink: 0;
}

.bnk-dialog-actions {
  display: flex; justify-content: flex-end;
  gap: 10px; padding: 0 20px 20px;
}
.bnk-btn-change {
  background: #2563eb !important;
  color: white !important;
  text-transform: none;
  border-radius: 8px !important;
}
</style>

